import { Button, Drawer, Empty, Message, Modal, Pagination, Spin, Table } from "@arco-design/web-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy } from "lucide-react";
import { useState } from "react";

import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import "../crmLegacySyncBoard.css";

type OutboxTableRef = {
  name: string;
  note: string;
};

type OutboxTask = {
  task_id: string;
  entity_id: string;
  entity_name: string;
  status: string;
  attempt_count: number;
  max_attempts: number;
  next_attempt_at: string;
  last_error: string | null;
  pending_dependencies: string | null;
  source_updated_at: string;
  updated_at: string;
};

type OutboxChannel = {
  key: string;
  title: string;
  summary: string;
  triggers: string[];
  new_tables: OutboxTableRef[];
  outbox_table: string;
  old_tables: OutboxTableRef[];
  max_attempts: number;
  total: number;
  page: number;
  page_size: number;
  counts: Record<string, number>;
  tasks: OutboxTask[];
  load_error: string | null;
};

type BrokenLegacyMappingItem = {
  channel: string;
  channel_title: string;
  entity_id: string;
  entity_name: string;
  legacy_id: string;
  issue: "empty_legacy_id" | "missing_old_row" | string;
  new_table: string;
  old_table: string;
  match_status?: string;
  matched_legacy_id?: string;
  match_candidates?: string[];
  match_key?: string;
};

type LegacyMappingReview = {
  items: BrokenLegacyMappingItem[];
  totals: Record<string, number>;
  load_errors: Record<string, string>;
};

type LegacyMappingProcessResult = {
  channel: string;
  channel_title: string;
  issue_count: number;
  actionable_count: number;
  enqueued_count: number;
  rebound_count?: number;
  created_count?: number;
  skipped_count: number;
  skipped_reason: string | null;
  action_summary: string;
};

const PROCESS_HINT: Record<string, string> = {
  major: "只处理未停用专业。按名称精确匹配旧专业；唯一且未被占用时回填并入队。同名多条需人工确认；无匹配则按固定 ID 入队补建旧专业。",
  organization: "只处理未停用学校。优先按省+市+校名匹配旧库；唯一命中则回填。无命中则入队在旧库新建并回填真实 legacy_id（高校→艺旅、中小学→中小学、企业→市场、机构/其他→社会）。同名多条需人工。",
  organization_unit: "只处理未停用组织单元。暂未开放按名匹配；仅 manual: 可重建写回。",
  contact: "只处理在岗且未删除联系人。优先按主手机号匹配旧库 WS_Cust.Phone；唯一命中则回填 legacy_id 并入队。同号多条需人工；无手机号或无匹配时，仅缺失旧行可重入队。",
  stay_area: "只处理未停用住宿点。按名称（可加省市）匹配旧住宿点；唯一命中回填。无命中则补 stay_area_id 并新建写回。",
  resource_point: "仅处理启用中的考察点（point_type=考察点）；按名称（可加省市）匹配旧库，唯一命中回填。无命中则投影新建。火车站/机场/车站不在本通道；已停用或非启用记录会跳过。",
};

const MATCH_STATUS_LABEL: Record<string, string> = {
  unique: "唯一可回填",
  ambiguous: "同名多条",
  none: "无匹配",
  unchecked: "未匹配",
};

const STATUS_LABEL: Record<string, string> = {
  pending: "待处理",
  running: "执行中",
  retry: "等待重试",
  succeeded: "已完成",
  dead: "已终止",
  awaiting_mapping: "等待映射",
  waiting_organization: "等待学校",
  partial: "部分完成",
  superseded: "已被新版本取代",
};

const CHANNEL_TITLE: Record<string, string> = {
  major: "专业",
  organization: "学校",
  organization_unit: "组织单元",
  contact: "联系人",
  stay_area: "住宿点",
  resource_point: "考察点",
};

const ISSUE_LABEL: Record<string, string> = {
  empty_legacy_id: "legacy_id 为空",
  missing_old_row: "旧库无对应 ID",
};

const STATUS_ORDER = [
  "pending",
  "running",
  "retry",
  "awaiting_mapping",
  "waiting_organization",
  "partial",
  "succeeded",
  "dead",
  "superseded",
];

const ACCENT: Record<string, string> = {
  organization: "#1f6f54",
  organization_unit: "#24548f",
  contact: "#8a5a20",
};

/** 读取写回旧库 Outbox 看板。 */
async function fetchCrmLegacySyncBoard(input: {
  channel: string;
  page: number;
  pageSize: number;
  includeTasks: boolean;
  channelOnly: boolean;
}): Promise<OutboxChannel[]> {
  const response = await httpClient.get<ApiEnvelope<{ channels: OutboxChannel[] }>>(
    "/data-governance/crm-legacy-sync/board",
    {
      params: {
        channel: input.channel,
        page: input.page,
        page_size: input.pageSize,
        include_tasks: input.includeTasks,
        channel_only: input.channelOnly,
      },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("CRM 同步看板读取失败");
  }
  return response.data.data.channels;
}

/** 读取指定通道（或全部）的 legacy_id 映射问题。 */
async function fetchLegacyMappingReview(channel?: string): Promise<LegacyMappingReview> {
  const response = await httpClient.get<ApiEnvelope<LegacyMappingReview>>(
    "/data-governance/crm-legacy-sync/legacy-mapping-review",
    {
      params: channel ? { channel } : undefined,
      // 联系人通道需扫新旧库数万 ID，默认 60s 易超时并反复重试，表现为抽屉一直转圈。
      timeout: 180_000,
      "axios-retry": { retries: 0 },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("legacy_id 映射核对失败");
  }
  return response.data.data;
}

/** 处理指定通道可自动治理的映射问题；可传 entityIds 只处理部分/单条。 */
async function processLegacyMapping(
  channel: string,
  entityIds?: string[],
): Promise<LegacyMappingProcessResult> {
  const search = new URLSearchParams();
  search.set("channel", channel);
  for (const entityId of entityIds ?? []) {
    const trimmed = entityId.trim();
    if (trimmed) search.append("entity_ids", trimmed);
  }
  const response = await httpClient.post<ApiEnvelope<LegacyMappingProcessResult>>(
    `/data-governance/crm-legacy-sync/legacy-mapping-process?${search.toString()}`,
    null,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("映射治理处理失败");
  }
  return response.data.data;
}

/** 把任务状态码转成页面文案。 */
function statusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status;
}

/** 状态计数的视觉分组。 */
function countTone(status: string): string {
  if (status === "dead") return "is-alert";
  if (status === "succeeded") return "is-ok";
  if (status === "retry" || status === "awaiting_mapping" || status === "waiting_organization" || status === "partial") {
    return "is-wait";
  }
  return "";
}

/** 复制表名到剪贴板。 */
async function copyTableName(tableName: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(tableName);
    Message.success(`已复制：${tableName}`);
  } catch {
    Message.error("复制失败，请手动选择后 Ctrl+C");
  }
}

/** 表名标签与复制按钮。 */
function TableNamePill({ name, note }: { name: string; note?: string }) {
  return (
    <span className="crm-outbox-pill-row">
      <span className="crm-outbox-pill" title={note}>{name}</span>
      <button
        type="button"
        className="crm-outbox-copy"
        aria-label={`复制 ${name}`}
        title={`复制 ${name}`}
        onClick={(event) => {
          event.stopPropagation();
          void copyTableName(name);
        }}
      >
        <Copy size={11} strokeWidth={2.2} />
      </button>
    </span>
  );
}

/** CRM 数据同步到旧系统的 Outbox 看板。 */
export function CrmLegacySyncPage() {
  const queryClient = useQueryClient();
  const [activeKey, setActiveKey] = useState("organization");
  const [page, setPage] = useState(1);
  const [governanceChannel, setGovernanceChannel] = useState<string | null>(null);
  const [processingChannel, setProcessingChannel] = useState<string | null>(null);
  const [processingEntityId, setProcessingEntityId] = useState<string | null>(null);
  const pageSize = 10;
  const overviewQuery = useQuery({
    queryKey: ["crm-legacy-sync-board-overview"],
    queryFn: () =>
      fetchCrmLegacySyncBoard({
        channel: "organization",
        page: 1,
        pageSize,
        includeTasks: false,
        channelOnly: false,
      }),
  });
  const detailQuery = useQuery({
    queryKey: ["crm-legacy-sync-board-detail", activeKey, page, pageSize],
    queryFn: () =>
      fetchCrmLegacySyncBoard({
        channel: activeKey,
        page,
        pageSize,
        includeTasks: true,
        channelOnly: true,
      }),
    enabled: Boolean(overviewQuery.data),
  });
  const mappingReviewQuery = useQuery({
    queryKey: ["crm-legacy-sync-mapping-review", governanceChannel],
    queryFn: () => fetchLegacyMappingReview(governanceChannel ?? undefined),
    enabled: Boolean(governanceChannel),
  });
  const processMutation = useMutation({
    mutationFn: ({
      channel,
      entityIds,
    }: {
      channel: string;
      entityIds?: string[];
    }) => processLegacyMapping(channel, entityIds),
    onSuccess: (result) => {
      const rebound = result.rebound_count ?? 0;
      const created = result.created_count ?? 0;
      const enqueued = result.enqueued_count;
      const skipped = result.skipped_count;
      const detail = result.skipped_reason ? `。${result.skipped_reason}` : "";
      if (rebound === 0 && created === 0 && enqueued === 0) {
        Message.warning(
          `${result.channel_title}：未改绑也未新建（跳过 ${skipped} 条）${detail}`,
        );
      } else {
        Message.success(
          `${result.channel_title}：回填 ${rebound} 条，新建 ${created} 条，入队 ${enqueued} 条，跳过 ${skipped} 条${detail}`,
        );
      }
      void queryClient.invalidateQueries({ queryKey: ["crm-legacy-sync-board-overview"] });
      void queryClient.invalidateQueries({ queryKey: ["crm-legacy-sync-board-detail"] });
      if (governanceChannel === result.channel) {
        void queryClient.invalidateQueries({
          queryKey: ["crm-legacy-sync-mapping-review", result.channel],
        });
      }
    },
    onError: () => {
      Message.error("处理失败，请稍后重试");
    },
    onSettled: () => {
      setProcessingChannel(null);
      setProcessingEntityId(null);
    },
  });
  const overviewChannels = overviewQuery.data ?? [];
  const detailChannel = detailQuery.data?.find((item) => item.key === activeKey);
  const channels = overviewChannels.map((channel) =>
    channel.key === activeKey && detailChannel
      ? {
          ...channel,
          counts: detailChannel.counts,
          total: detailChannel.total,
          page: detailChannel.page,
          page_size: detailChannel.page_size,
          load_error: detailChannel.load_error,
        }
      : channel,
  );
  const activeMeta = channels.find((item) => item.key === activeKey) ?? channels[0];
  const activeTasks = detailChannel?.tasks ?? [];
  const activeTotal = detailChannel?.total ?? activeMeta?.total ?? 0;
  const activeLoadError = detailChannel?.load_error ?? null;
  const refreshing = overviewQuery.isFetching || detailQuery.isFetching;
  const mappingItems = mappingReviewQuery.data?.items ?? [];
  const mappingTotals = mappingReviewQuery.data?.totals ?? {};
  const mappingLoadErrors = mappingReviewQuery.data?.load_errors ?? {};
  const mappingTotalCount = mappingItems.length;
  const governanceTitle = governanceChannel
    ? `${CHANNEL_TITLE[governanceChannel] ?? governanceChannel} · legacy_id 映射治理`
    : "legacy_id 映射治理";

  const handleRefresh = () => {
    void overviewQuery.refetch();
    void detailQuery.refetch();
  };

  const openGovernance = (channelKey: string) => {
    setActiveKey(channelKey);
    setGovernanceChannel(channelKey);
  };

  const confirmProcess = (channelKey: string, entityIds?: string[]) => {
    const title = CHANNEL_TITLE[channelKey] ?? channelKey;
    const single = entityIds?.length === 1;
    Modal.confirm({
      title: single ? `处理该条${title}映射` : `处理${title}映射问题`,
      content: single
        ? "仅处理当前这一条：唯一命中则回填，否则按通道兜底规则处理。"
        : (PROCESS_HINT[channelKey] ?? "将对该通道可自动治理的记录登记写回任务。"),
      okText: "开始处理",
      onOk: () => {
        setProcessingChannel(channelKey);
        setProcessingEntityId(single ? entityIds![0] : null);
        return processMutation.mutateAsync({ channel: channelKey, entityIds });
      },
    });
  };

  return (
    <section className="crm-outbox-board">
      <div className="crm-outbox-lead">
        <p>
          学校、组织单元、联系人、住宿点、考察点和专业保存在新库提交时写入 Outbox。接口成功只表示新库和任务已提交，旧 SQL Server 由后台 worker 异步写回。写失败最多重试 8 次，之后进入已终止。每个通道可单独做映射核对与处理。
        </p>
        <div className="crm-outbox-lead-actions">
          <Button loading={refreshing} onClick={handleRefresh}>
            刷新
          </Button>
        </div>
      </div>
      {overviewQuery.isLoading ? <Spin /> : null}
      {overviewQuery.isError ? <p className="crm-outbox-error">看板读取失败，请稍后刷新。</p> : null}
      <div className="crm-outbox-grid">
        {channels.map((channel) => (
          <div
            key={channel.key}
            role="button"
            tabIndex={0}
            className={`crm-outbox-card${activeMeta?.key === channel.key ? " is-active" : ""}`}
            style={{ ["--crm-outbox-accent" as string]: ACCENT[channel.key] ?? "#24548f" }}
            onClick={() => {
              setActiveKey(channel.key);
              setPage(1);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setActiveKey(channel.key);
                setPage(1);
              }
            }}
          >
            <div className="crm-outbox-card-head">
              <div>
                <p className="crm-outbox-kicker">{channel.triggers.join(" / ")}</p>
                <h2>{channel.title}</h2>
              </div>
              <div className="crm-outbox-card-actions">
                <Button
                  size="mini"
                  type="outline"
                  onClick={(event) => {
                    event.stopPropagation();
                    openGovernance(channel.key);
                  }}
                >
                  数据治理
                </Button>
                <Button
                  size="mini"
                  type="primary"
                  loading={processingChannel === channel.key && processMutation.isPending}
                  onClick={(event) => {
                    event.stopPropagation();
                    confirmProcess(channel.key);
                  }}
                >
                  处理
                </Button>
              </div>
            </div>
            <p className="crm-outbox-summary">{channel.summary}</p>
            <div className="crm-outbox-flow">
              <div className="crm-outbox-node is-new">
                <strong>新库</strong>
                {channel.new_tables.map((table) => (
                  <TableNamePill key={table.name} name={table.name} note={table.note} />
                ))}
              </div>
              <span className="crm-outbox-arrow" aria-hidden>↓</span>
              <div className="crm-outbox-node is-outbox">
                <strong>Outbox</strong>
                <TableNamePill name={channel.outbox_table} note="同一事务登记，worker 异步消费" />
              </div>
              <span className="crm-outbox-arrow" aria-hidden>↓</span>
              <div className="crm-outbox-node is-old">
                <strong>旧库</strong>
                {channel.old_tables.map((table) => (
                  <TableNamePill key={table.name} name={table.name} note={table.note} />
                ))}
              </div>
            </div>
            <div className="crm-outbox-counts">
              {STATUS_ORDER.filter((status) => (channel.counts[status] ?? 0) > 0).map((status) => (
                <span key={status} className={`crm-outbox-count ${countTone(status)}`}>
                  {statusLabel(status)} {channel.counts[status]}
                </span>
              ))}
              {Object.values(channel.counts).every((count) => count === 0) ? (
                <span className="crm-outbox-count">暂无任务</span>
              ) : null}
            </div>
          </div>
        ))}
      </div>
      {activeMeta ? (
        <div className="crm-outbox-detail">
          <div className="crm-outbox-detail-head">
            <div>
              <h2>{activeMeta.title}同步详情</h2>
              <p>
                Outbox {activeMeta.outbox_table} · 共 {activeTotal} 条 · 失败上限 {activeMeta.max_attempts} 次
              </p>
            </div>
          </div>
          {activeLoadError ? <p className="crm-outbox-error">{activeLoadError}</p> : null}
          <Table
            rowKey="task_id"
            loading={detailQuery.isFetching}
            pagination={false}
            data={activeTasks}
            noDataElement={<Empty description="这张 Outbox 还没有任务" />}
            columns={[
              { title: "对象", dataIndex: "entity_name", width: 180 },
              {
                title: "新库 ID",
                dataIndex: "entity_id",
                width: 340,
                render: (entityId: string) => (
                  <span className="crm-outbox-id" title={entityId}>
                    {entityId}
                  </span>
                ),
              },
              {
                title: "状态",
                dataIndex: "status",
                width: 140,
                render: (status: string) => (
                  <span className={`crm-outbox-status is-${status}`}>{statusLabel(status)}</span>
                ),
              },
              {
                title: "尝试",
                width: 90,
                render: (_value, record) => `${record.attempt_count}/${record.max_attempts}`,
              },
              { title: "来源版本", dataIndex: "source_updated_at", width: 170 },
              { title: "下次尝试", dataIndex: "next_attempt_at", width: 170 },
              {
                title: "说明",
                render: (_value, record) => record.last_error || record.pending_dependencies || (
                  <span className="crm-outbox-muted">—</span>
                ),
              },
            ]}
          />
          <div className="crm-outbox-pager">
            <Pagination
              current={page}
              pageSize={pageSize}
              total={activeTotal}
              showTotal
              onChange={(nextPage) => setPage(nextPage)}
            />
          </div>
        </div>
      ) : null}
      <Drawer
        width={1120}
        title={governanceTitle}
        visible={Boolean(governanceChannel)}
        footer={null}
        unmountOnExit
        onCancel={() => setGovernanceChannel(null)}
      >
        <div className="crm-outbox-governance">
          <p className="crm-outbox-governance-lead">
            仅核对当前通道。空/失效的 legacy_id 会尝试自动匹配旧库主表：
            学校/住宿/考察按名称，联系人按主手机号。唯一命中可自动回填；多条需人工。
            {governanceChannel ? ` ${PROCESS_HINT[governanceChannel] ?? ""}` : ""}
          </p>
          <div className="crm-outbox-governance-actions">
            <Button
              loading={mappingReviewQuery.isFetching}
              onClick={() => {
                void mappingReviewQuery.refetch();
              }}
            >
              重新核对
            </Button>
            <Button
              type="primary"
              loading={
                Boolean(governanceChannel)
                && processingChannel === governanceChannel
                && processMutation.isPending
              }
              onClick={() => {
                if (governanceChannel) confirmProcess(governanceChannel);
              }}
            >
              处理本通道
            </Button>
            <span className="crm-outbox-muted">
              {mappingReviewQuery.isFetching && !mappingReviewQuery.data
                ? "正在核对旧库映射，联系人通道首次可能需要几十秒…"
                : `问题合计 ${mappingTotalCount} 条`}
            </span>
          </div>
          {Object.keys(mappingTotals).length > 0 ? (
            <div className="crm-outbox-counts">
              {Object.entries(mappingTotals).map(([channel, count]) => (
                <span
                  key={channel}
                  className={`crm-outbox-count${count > 0 ? " is-alert" : " is-ok"}`}
                >
                  {CHANNEL_TITLE[channel] ?? channel} {count}
                </span>
              ))}
            </div>
          ) : null}
          {Object.values(mappingLoadErrors).length > 0 ? (
            <div className="crm-outbox-governance-errors">
              {Object.entries(mappingLoadErrors).map(([channel, message]) => (
                <p key={channel} className="crm-outbox-error">
                  {channel}：{message}
                </p>
              ))}
            </div>
          ) : null}
          {mappingReviewQuery.isError ? (
            <p className="crm-outbox-error">映射核对失败，请稍后重试。</p>
          ) : null}
          <Table
            className="crm-outbox-governance-table"
            rowKey={(record) => `${record.channel}-${record.entity_id}-${record.issue}-${record.legacy_id}`}
            loading={mappingReviewQuery.isFetching}
            pagination={{ pageSize: 20, showTotal: true }}
            data={mappingItems}
            scroll={{ x: 1560 }}
            noDataElement={
              <Empty
                description={
                  mappingReviewQuery.isFetching
                    ? "正在核对旧库映射…"
                    : "未发现需要治理的映射问题"
                }
              />
            }
            columns={[
              {
                title: "操作",
                width: 88,
                fixed: "left" as const,
                render: (_value, record: BrokenLegacyMappingItem) => (
                  <Button
                    size="mini"
                    type="primary"
                    loading={
                      processMutation.isPending
                      && processingEntityId === record.entity_id
                    }
                    onClick={() => {
                      if (governanceChannel) {
                        confirmProcess(governanceChannel, [record.entity_id]);
                      }
                    }}
                  >
                    处理
                  </Button>
                ),
              },
              { title: "通道", dataIndex: "channel_title", width: 88, ellipsis: true },
              {
                title: "问题",
                dataIndex: "issue",
                width: 120,
                ellipsis: true,
                render: (issue: string) => ISSUE_LABEL[issue] ?? issue,
              },
              {
                title: "匹配",
                dataIndex: "match_status",
                width: 110,
                ellipsis: true,
                render: (status: string | undefined) => MATCH_STATUS_LABEL[status ?? "unchecked"] ?? status,
              },
              {
                title: "名称",
                dataIndex: "entity_name",
                width: 180,
                ellipsis: true,
                render: (name: string) => (
                  <span className="crm-outbox-governance-text" title={name}>
                    {name}
                  </span>
                ),
              },
              {
                title: "当前 legacy_id",
                dataIndex: "legacy_id",
                width: 220,
                ellipsis: true,
                render: (legacyId: string) => (
                  <span className="crm-outbox-id" title={legacyId || "（空）"}>
                    {legacyId || "（空）"}
                  </span>
                ),
              },
              {
                title: "建议回填",
                dataIndex: "matched_legacy_id",
                width: 220,
                ellipsis: true,
                render: (_value: string, record: BrokenLegacyMappingItem) => {
                  const text =
                    record.matched_legacy_id
                    || (record.match_candidates?.length
                      ? record.match_candidates.slice(0, 3).join(" / ")
                      : "—");
                  return (
                    <span className="crm-outbox-id" title={record.match_key || text}>
                      {text}
                    </span>
                  );
                },
              },
              {
                title: "新库 ID",
                dataIndex: "entity_id",
                width: 160,
                ellipsis: true,
                render: (entityId: string) => (
                  <span className="crm-outbox-id" title={entityId}>
                    {entityId}
                  </span>
                ),
              },
              {
                title: "匹配说明",
                dataIndex: "match_key",
                width: 180,
                ellipsis: true,
                render: (key: string | undefined) => key || "—",
              },
            ]}
          />
        </div>
      </Drawer>
    </section>
  );
}
